import { useEffect, useState } from "react";
import { Alert, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import type { MyApplication, PortalApi } from "../api/portalApi";
import { brandedIconFor } from "../lib/appIcons";

const DEFAULT_ACCENT = "#0A51A3"; // TTS blue

function AppCard({ app }: { app: MyApplication }) {
  const accent = app.accentColor ?? DEFAULT_ACCENT;
  const branded = brandedIconFor(app.key);
  return (
    <Card className="h-100 shadow-sm position-relative" style={{ borderTop: `4px solid ${accent}` }}>
      <Card.Body className="text-center p-4">
        {branded ? (
          <img
            src={branded}
            alt=""
            className="d-inline-block rounded-3 mb-3"
            style={{ width: 72, height: 72 }}
          />
        ) : (
          <div
            className="d-inline-flex justify-content-center align-items-center rounded-circle mb-3"
            style={{ width: 64, height: 64, backgroundColor: `${accent}1a`, color: accent }}
          >
            <i className={`bi bi-${app.iconName ?? "app"} fs-3`} aria-hidden="true" />
          </div>
        )}
        <Card.Title className="fs-6 mb-1">
          <a
            href={app.launchUrl}
            target="_blank"
            rel="noopener"
            className="stretched-link text-decoration-none text-body"
          >
            {app.displayName}
          </a>
        </Card.Title>
        {app.description && (
          <Card.Text className="text-muted small mb-0">{app.description}</Card.Text>
        )}
      </Card.Body>
    </Card>
  );
}

export default function HomePage({ api }: { api: PortalApi }) {
  const [apps, setApps] = useState<MyApplication[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setApps(await api.listMyApplications());
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [api]);

  return (
    <Container className="pb-5">
      <h3 className="mb-1">Applications</h3>
      <p className="text-muted mb-4">Everything you have access to, one click away.</p>

      {error && <Alert variant="danger">{error}</Alert>}

      {!apps && !error && (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
        </div>
      )}

      {apps && apps.length === 0 && (
        <Alert variant="light" className="border text-center py-5">
          <i className="bi bi-inbox fs-1 d-block mb-2 text-muted" aria-hidden="true" />
          <div className="fw-semibold">No applications assigned</div>
          <div className="text-muted">
            You&apos;re signed in, but no application access has been granted yet. Contact a TTS
            administrator if you believe this is a mistake.
          </div>
        </Alert>
      )}

      {apps && apps.length > 0 && (
        <Row xs={1} sm={2} lg={3} xl={4} className="g-3">
          {apps.map((app) => (
            <Col key={app.key}>
              <AppCard app={app} />
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
}
